import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Button, Form, Modal, OverlayTrigger, Table, Tooltip} from 'react-bootstrap'
import {SingleValue} from 'react-select'
import AsyncSelect from 'react-select/async'
import Swal from 'sweetalert2'
import {
  mondayApiService,
  MondayBoardMapping,
  MondayBoardOption,
} from '../services/mondayApi'
import {SelectAutoComplete} from '../../../../_metronic/partials/components/Select/SelectAutoComplete'

interface BoardMappingForm {
  id: number | null
  boardId: string
  boardName: string
  projectId: number | null
  projectName: string
}

interface MondayManageModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

const swalThemeBackground = () =>
  (window.localStorage.getItem('kt_theme_mode_value') || '') === 'dark' ? '#353b48' : '#fff'

const toFormMappings = (mappingsData: MondayBoardMapping[]): BoardMappingForm[] =>
  mappingsData.map((m) => ({
    id: m.id,
    boardId: m.board_id,
    boardName: m.board_name || m.board_id,
    projectId: m.project_id,
    projectName: m.project_name || '',
  }))

const normalizeForm = (rows: BoardMappingForm[]) =>
  rows
    .map((m) => ({
      id: m.id && m.id > 0 ? m.id : null,
      boardId: m.boardId,
      projectId: m.projectId,
    }))
    .sort((a, b) => {
      const aKey = `${a.id ?? 'new'}-${a.boardId}`
      const bKey = `${b.id ?? 'new'}-${b.boardId}`
      return aKey.localeCompare(bKey)
    })

type BoardSelectOption = {value: string; label: string}

const toSelectOptions = (
  boards: MondayBoardOption[],
  excludedBoardIds: string[],
  currentValue: BoardSelectOption | null
): BoardSelectOption[] => {
  const excluded = new Set(excludedBoardIds)
  const options = boards
    .filter((board) => !excluded.has(board.id))
    .map((board) => ({
      value: board.id,
      label: board.name,
    }))

  if (currentValue && !options.some((o) => o.value === currentValue.value)) {
    options.unshift(currentValue)
  }

  return options
}

interface MondayBoardSelectProps {
  mappingId: number | null
  value: BoardSelectOption | null
  excludedBoardIds: string[]
  defaultBoards: MondayBoardOption[]
  isDisabled?: boolean
  onChange: (option: SingleValue<BoardSelectOption>) => void
}

const MondayBoardSelect: FC<MondayBoardSelectProps> = ({
  mappingId,
  value,
  excludedBoardIds,
  defaultBoards,
  isDisabled,
  onChange,
}) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const excludedRef = useRef(excludedBoardIds)
  const valueRef = useRef(value)

  useEffect(() => {
    excludedRef.current = excludedBoardIds
  }, [excludedBoardIds])

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const defaultOptions = useMemo(
    () => toSelectOptions(defaultBoards, excludedBoardIds, value),
    [defaultBoards, excludedBoardIds, value]
  )

  const loadOptions = useCallback(
    (inputValue: string): Promise<BoardSelectOption[]> => {
      const query = inputValue.trim()

      if (query === '') {
        return Promise.resolve(toSelectOptions(defaultBoards, excludedRef.current, valueRef.current))
      }

      return new Promise((resolve) => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current)
        }

        const requestId = ++requestIdRef.current

        debounceRef.current = setTimeout(async () => {
          try {
            const boards = await mondayApiService.searchBoards(query)
            if (requestId !== requestIdRef.current) {
              resolve([])
              return
            }

            resolve(toSelectOptions(boards, excludedRef.current, valueRef.current))
          } catch (err) {
            console.error('Erro ao buscar boards:', err)
            resolve([])
          }
        }, 400)
      })
    },
    [defaultBoards]
  )

  return (
    <AsyncSelect
      key={`board-select-${mappingId}`}
      cacheOptions={false}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      value={value}
      onChange={onChange}
      placeholder={
        isDisabled && defaultBoards.length === 0 ? 'Carregando boards...' : 'Buscar board...'
      }
      isDisabled={isDisabled}
      isClearable
      isSearchable
      noOptionsMessage={({inputValue}) =>
        inputValue ? 'Nenhum board encontrado' : 'Digite para buscar boards'
      }
      loadingMessage={() => 'Buscando...'}
      menuPortalTarget={document.body}
      menuPosition='fixed'
      styles={{
        menuPortal: (base) => ({...base, zIndex: 9999}),
        valueContainer: (styles) => ({
          ...styles,
          padding: '2px 8px',
        }),
        input: (styles) => ({
          ...styles,
          margin: '0px',
          padding: '0px',
        }),
        option: (styles, {isDisabled: optDisabled, isFocused, isSelected}) => ({
          ...styles,
          backgroundColor: optDisabled
            ? undefined
            : isSelected
              ? '#b5d44e70'
              : isFocused
                ? '#b5d44e70'
                : undefined,
          color: optDisabled ? '#ccc' : isSelected || isFocused ? '#181C32' : undefined,
        }),
      }}
      classNames={{
        control: () => 'form-control form-control-solid py-1',
        input: () => 'text-dark',
        singleValue: () => 'text-dark',
        menu: () => 'bg-body',
      }}
    />
  )
}

const MondayManageModal: FC<MondayManageModalProps> = ({show, onClose, onSuccess}) => {
  const [mappings, setMappings] = useState<BoardMappingForm[]>([])
  const [existingMappings, setExistingMappings] = useState<MondayBoardMapping[]>([])
  const [savedSnapshot, setSavedSnapshot] = useState<BoardMappingForm[]>([])
  const [defaultBoards, setDefaultBoards] = useState<MondayBoardOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingBoards, setIsLoadingBoards] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextId, setNextId] = useState(1)

  const applyLoadedMappings = (mappingsData: MondayBoardMapping[]) => {
    setExistingMappings(mappingsData)
    const formMappings = toFormMappings(mappingsData)
    setMappings(formMappings)
    setSavedSnapshot(formMappings)

    if (formMappings.length > 0) {
      const maxId = Math.max(...formMappings.map((m) => m.id || 0))
      setNextId(maxId + 1)
    } else {
      setNextId(1)
    }
  }

  const loadBoardsInBackground = useCallback(async () => {
    setIsLoadingBoards(true)
    try {
      const boardsData = await mondayApiService.searchBoards('')
      setDefaultBoards(boardsData)
    } catch (err) {
      console.error('Erro ao carregar boards do Monday.com:', err)
    } finally {
      setIsLoadingBoards(false)
    }
  }, [])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const mappingsData = await mondayApiService.getBoardMappings()
      applyLoadedMappings(mappingsData)
      setIsLoading(false)
      void loadBoardsInBackground()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar mapeamentos Monday.com'
      setError(message)
      setIsLoading(false)
    }
  }, [loadBoardsInBackground])

  useEffect(() => {
    if (show) {
      loadData()
    }
  }, [show, loadData])

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(normalizeForm(mappings)) !== JSON.stringify(normalizeForm(savedSnapshot))
  }, [mappings, savedSnapshot])

  const hasIncompleteRows = useMemo(
    () => mappings.some((m) => Boolean(m.boardId) !== Boolean(m.projectId)),
    [mappings]
  )

  const savedMappingsCount = useMemo(
    () => savedSnapshot.filter((m) => m.boardId && m.projectId && m.id && m.id > 0).length,
    [savedSnapshot]
  )

  const importDisabledReason = useMemo(() => {
    if (isLoading) return 'Aguarde o carregamento dos mapeamentos.'
    if (isSubmitting) return 'Aguarde o salvamento dos mapeamentos.'
    if (isImporting) return 'Importação em andamento.'
    if (hasUnsavedChanges) {
      return 'Há alterações não salvas no mapeamento. Salve antes de importar.'
    }
    if (hasIncompleteRows) {
      return 'Há linhas incompletas. Preencha Board e Projeto ou remova a linha.'
    }
    if (savedMappingsCount === 0) {
      return 'Configure e salve ao menos um mapeamento Board → Projeto antes de importar.'
    }
    return null
  }, [isLoading, isSubmitting, isImporting, hasUnsavedChanges, hasIncompleteRows, savedMappingsCount])

  const canImport = importDisabledReason === null

  const handleClose = () => {
    setError(null)
    setMappings([])
    setExistingMappings([])
    setSavedSnapshot([])
    setDefaultBoards([])
    setIsLoadingBoards(false)
    onClose()
  }

  const handleAddMapping = () => {
    const newMapping: BoardMappingForm = {
      id: -nextId,
      boardId: '',
      boardName: '',
      projectId: null,
      projectName: '',
    }
    setMappings([...mappings, newMapping])
    setNextId(nextId + 1)
  }

  const handleRemoveMapping = (id: number | null) => {
    setMappings(mappings.filter((m) => m.id !== id))
  }

  const handleMappingChange = (
    id: number | null,
    field: keyof BoardMappingForm,
    value: unknown
  ) => {
    setMappings(
      mappings.map((m) => {
        if (m.id !== id) return m

        if (field === 'boardId') {
          const option = value as {value: string; label: string} | null
          return {
            ...m,
            boardId: option?.value || '',
            boardName: option?.label || '',
          }
        }

        if (field === 'projectId') {
          if (
            !value ||
            (typeof value === 'object' &&
              (!('value' in (value as object)) || !(value as {value?: string}).value))
          ) {
            return {...m, projectId: null, projectName: ''}
          }

          const projectValue =
            typeof value === 'object' &&
            value !== null &&
            'value' in value &&
            (value as {value?: string}).value
              ? Number((value as {value: string}).value)
              : null
          const projectLabel =
            typeof value === 'object' && value !== null && 'label' in value
              ? String((value as {label?: string}).label || '')
              : ''

          return {
            ...m,
            projectId: projectValue && !isNaN(projectValue) ? projectValue : null,
            projectName: projectLabel,
          }
        }

        return {...m, [field]: value as never}
      })
    )
  }

  const persistMappings = async (): Promise<boolean> => {
    if (hasIncompleteRows) {
      setError('Preencha Board e Projeto em todas as linhas ou remova as incompletas.')
      return false
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const validMappings = mappings.filter((m) => m.boardId && m.projectId)
      const existingIds = mappings
        .filter((m) => m.id !== null && m.id > 0)
        .map((m) => m.id as number)
      const toDelete = existingMappings.filter((m) => !existingIds.includes(m.id))

      for (const mapping of toDelete) {
        await mondayApiService.deleteBoardMapping(mapping.id)
      }

      for (const mapping of validMappings) {
        if (mapping.id === null || mapping.id < 0) {
          await mondayApiService.createBoardMapping({
            board_id: mapping.boardId,
            board_name: mapping.boardName,
            project_id: mapping.projectId!,
          })
        } else {
          await mondayApiService.updateBoardMapping(mapping.id, {
            project_id: mapping.projectId!,
            board_name: mapping.boardName,
          })
        }
      }

      const refreshed = await mondayApiService.getBoardMappings()
      applyLoadedMappings(refreshed)
      onSuccess()
      return true
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar mapeamentos'
      setError(message)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await persistMappings()
  }

  const handleImport = async () => {
    if (!canImport || isImporting) return

    try {
      setIsImporting(true)
      setError(null)
      const result = await mondayApiService.importItems()

      await Swal.fire({
        icon: 'success',
        title: 'Importação enfileirada',
        html: `
          <div class="text-start">
            <p class="mb-2">${result.message}</p>
            <p class="text-muted mb-0">
              A importação está rodando em segundo plano. Apenas itens de boards mapeados serão importados.
            </p>
          </div>
        `,
        background: swalThemeBackground(),
        confirmButtonText: 'Entendi',
        customClass: {confirmButton: 'btn btn-primary'},
        buttonsStyling: false,
      })
    } catch (err: unknown) {
      const text = err instanceof Error ? err.message : 'Erro ao enfileirar importação'
      setError(text)
      await Swal.fire({
        icon: 'error',
        title: 'Não foi possível importar',
        text,
        background: swalThemeBackground(),
        confirmButtonText: 'Ok',
        customClass: {confirmButton: 'btn btn-danger'},
        buttonsStyling: false,
      })
    } finally {
      setIsImporting(false)
    }
  }

  const importButton = (
    <Button
      variant='primary'
      type='button'
      onClick={handleImport}
      disabled={!canImport}
      style={!canImport ? {pointerEvents: 'none'} : undefined}
    >
      {isImporting ? (
        <>
          <span className='spinner-border spinner-border-sm me-2' role='status' />
          Enfileirando...
        </>
      ) : (
        <>
          <i className='fas fa-download me-2'></i>
          Importar itens
        </>
      )}
    </Button>
  )

  return (
    <Modal show={show} onHide={handleClose} size='lg' centered backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>Gerenciar mapeamentos Monday.com</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {isLoading && mappings.length === 0 ? (
            <div className='text-center py-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Carregando...</span>
              </div>
              <p className='text-muted mt-3 mb-0'>Carregando mapeamentos...</p>
            </div>
          ) : (
            <>
              <p className='text-muted mb-4'>
                Mapeie boards do Monday.com para projetos do TaskRush. Ao salvar, os webhooks são
                registrados automaticamente. Depois, importe os itens existentes.
              </p>

              {error && (
                <Alert variant='danger' className='mb-4'>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  {error}
                </Alert>
              )}

              {hasUnsavedChanges && (
                <Alert variant='warning' className='mb-4'>
                  <i className='fas fa-exclamation-circle me-2'></i>
                  Há alterações não salvas. Salve o mapeamento antes de importar.
                </Alert>
              )}

              {isLoadingBoards && (
                <Alert variant='light' className='mb-4 d-flex align-items-center'>
                  <span
                    className='spinner-border spinner-border-sm text-primary me-3'
                    role='status'
                    aria-hidden='true'
                  />
                  <span>
                    Buscando boards no Monday.com... Os campos de Board ficam temporariamente
                    desabilitados.
                  </span>
                </Alert>
              )}

              <div className='d-flex align-items-center justify-content-between mb-4'>
                <h5 className='mb-0'>Mapeamento Board → Projeto</h5>
                <Button
                  variant='outline-primary'
                  size='sm'
                  type='button'
                  onClick={handleAddMapping}
                  disabled={isSubmitting || isLoading || isImporting}
                >
                  <i className='fas fa-plus me-2'></i>
                  Adicionar mapeamento
                </Button>
              </div>

              {mappings.length === 0 ? (
                <div className='text-center py-5 text-muted'>
                  <i className='fas fa-inbox fs-2x mb-3 d-block'></i>
                  <p>Nenhum mapeamento configurado</p>
                  <p className='mb-0'>
                    Clique em &quot;Adicionar mapeamento&quot; para começar
                  </p>
                </div>
              ) : (
                <div className='table-responsive'>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Board Monday.com</th>
                        <th>Projeto no TaskRush</th>
                        <th style={{width: '50px'}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((mapping, index) => {
                        const excludedBoardIds = mappings
                          .filter((m) => m.id !== mapping.id && m.boardId)
                          .map((m) => m.boardId)

                        return (
                          <tr key={mapping.id ?? `new-${index}`}>
                            <td>
                              <MondayBoardSelect
                                mappingId={mapping.id}
                                value={
                                  mapping.boardId
                                    ? {
                                        value: mapping.boardId,
                                        label: mapping.boardName || mapping.boardId,
                                      }
                                    : null
                                }
                                excludedBoardIds={excludedBoardIds}
                                defaultBoards={defaultBoards}
                                onChange={(newValue) => {
                                  handleMappingChange(mapping.id, 'boardId', newValue)
                                }}
                                isDisabled={isSubmitting || isImporting || isLoadingBoards}
                              />
                            </td>
                            <td>
                              <SelectAutoComplete
                                endPoint='projetos'
                                value={
                                  mapping.projectId
                                    ? {
                                        value: mapping.projectId.toString(),
                                        label: mapping.projectName || '',
                                      }
                                    : undefined
                                }
                                placeholder='Selecione um projeto'
                                onChange={(newValue) => {
                                  handleMappingChange(mapping.id, 'projectId', newValue)
                                }}
                                isDisabled={isSubmitting || isImporting}
                              />
                            </td>
                            <td className='text-center'>
                              <Button
                                variant='link'
                                className='text-danger p-0'
                                type='button'
                                onClick={() => handleRemoveMapping(mapping.id)}
                                disabled={isSubmitting || isImporting}
                                title='Remover mapeamento'
                              >
                                <i className='fas fa-trash'></i>
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className='d-flex justify-content-between flex-wrap gap-2'>
          <Button variant='light' onClick={handleClose} disabled={isSubmitting || isImporting}>
            Fechar
          </Button>

          <div className='d-flex gap-2 flex-wrap'>
            <Button
              variant='primary'
              type='submit'
              disabled={isSubmitting || isLoading || isImporting || !hasUnsavedChanges}
            >
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status' />
                  Salvando...
                </>
              ) : (
                'Salvar mapeamentos'
              )}
            </Button>

            {importDisabledReason ? (
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='monday-import-disabled-tooltip'>{importDisabledReason}</Tooltip>
                }
              >
                <span className='d-inline-block'>{importButton}</span>
              </OverlayTrigger>
            ) : (
              importButton
            )}
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default MondayManageModal
